import { t } from '@lingui/macro'
import { Close } from '@mui/icons-material'
import { IconButton, TextField } from '@mui/material'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface StableSearchInputProps {
    onSearchChange: (search: string) => void
    initialValue?: string
    placeholder?: string
}

export const StableSearchInput = React.memo<StableSearchInputProps>(({ onSearchChange, initialValue = '', placeholder }) => {
    const [value, setValue] = useState(initialValue)
    const inputRef = useRef<HTMLInputElement>(null)

    const debouncedSearchChange = useRef(
        debounce((newSearch: string) => {
            onSearchChange(newSearch)
        }, 300)
    ).current

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
            setValue(newValue)
            debouncedSearchChange(newValue)
        },
        [debouncedSearchChange]
    )

    const handleClear = useCallback(() => {
        setValue('')
        debouncedSearchChange('')
        inputRef.current?.focus()
    }, [debouncedSearchChange])

    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel()
        }
    }, [debouncedSearchChange])

    return (
        <TextField
            label={t`Filter`}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            size='small'
            className='rounded-md bg-white'
            inputRef={inputRef}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '8px'
                }
            }}
            inputProps={{
                'data-testid': 'source-filter-input'
            }}
            InputProps={{
                endAdornment: value && (
                    <IconButton
                        aria-label='clear'
                        data-testid='clear-filter-button'
                        onClick={handleClear}
                        size='small'
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <Close fontSize='small' />
                    </IconButton>
                )
            }}
        />
    )
})

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
StableSearchInput.displayName = 'StableSearchInput'
